/**
 * RequestManager — Concurrency limiter + abort + dedupe for API calls.
 *
 * TASK A: Max 4 concurrent in-flight. FIFO queue. Writes get priority.
 * TASK B: AbortController — cancel groups on route/filter change.
 * TASK C: Dedupe identical in-flight GETs (same URL+params → reuse promise).
 */

import { prepareSaveIntent, signalSaveStatus } from './saveIntent';

const MAX_CONCURRENT = 4;
let inflight = 0;
const queue = [];               // { run, priority, resolve, reject, label }
const inflightGets = new Map(); // dedupeKey → Promise
const cancelGroups = new Map(); // groupId → Set<AbortController>

// ── Telemetry log ring (last 200 events) ────────────────────────────────
const _log = [];
const LOG_CAP = 200;
let _seqId = 0;
function _emit(type, detail) {
  const entry = { seq: ++_seqId, ts: Date.now(), type, inflight, queued: queue.length, deduped: inflightGets.size, ...detail };
  _log.push(entry);
  if (_log.length > LOG_CAP) _log.shift();
  if (typeof window !== 'undefined' && window.__WEB6P_VERBOSE) {
    console.log(`[RM] ${type}`, entry);
  }
}

// ── Queue drain ─────────────────────────────────────────────────────────
function drain() {
  if (inflight >= MAX_CONCURRENT || queue.length === 0) return;
  // Priority sort: writes (1) before reads (0)
  queue.sort((a, b) => b.priority - a.priority);
  const item = queue.shift();
  inflight++;
  _emit('DEQUEUE', { label: item.label });
  item.run()
    .then(item.resolve)
    .catch(item.reject)
    .finally(() => { inflight--; _emit('COMPLETE', { label: item.label }); drain(); });
}

// ── Dedupe key ──────────────────────────────────────────────────────────
function dedupeKey(method, url, params) {
  if (method !== 'get') return null;
  const p = params ? JSON.stringify(params, Object.keys(params).sort()) : '';
  return `GET:${url}|${p}`;
}

// ── Cancel groups ───────────────────────────────────────────────────────
export function createCancelGroup(groupId) {
  cancelGroup(groupId);           // abort any lingering controllers
  cancelGroups.set(groupId, new Set());
  _emit('GROUP_CREATE', { groupId });
  return groupId;
}

export function cancelGroup(groupId) {
  const ctrls = cancelGroups.get(groupId);
  if (ctrls && ctrls.size > 0) {
    const count = ctrls.size;
    ctrls.forEach(c => { try { c.abort(); } catch {} });
    cancelGroups.delete(groupId);
    _emit('GROUP_CANCEL', { groupId, aborted: count });
  } else {
    cancelGroups.delete(groupId);
  }
}

// ── Managed request ─────────────────────────────────────────────────────
export function managedRequest(axiosInstance, config) {
  const method = (config.method || 'get').toLowerCase();
  const isWrite = ['post', 'put', 'delete', 'patch'].includes(method);
  const dk = dedupeKey(method, config.url, config.params);
  const label = `${method.toUpperCase()} ${(config.url || '').replace(/^https?:\/\/[^/]+/, '')}`;

  // TASK C: Dedupe — reuse in-flight GET
  if (dk && inflightGets.has(dk)) {
    _emit('DEDUPE_HIT', { label });
    return inflightGets.get(dk);
  }

  // TASK B: AbortController
  const controller = new AbortController();
  if (!config.signal) config.signal = controller.signal;
  if (config._cancelGroup) {
    const group = cancelGroups.get(config._cancelGroup);
    if (group) group.add(controller);
  }

  const run = async () => {
    const clearIntent = await prepareSaveIntent(config);
    try {
      const response = await axiosInstance.request(config);
      if (response.status >= 200 && response.status < 300) clearIntent?.();
      if (!String(config.url).includes('/auth/') && !String(config.url).includes('/business-commands')) {
        signalSaveStatus('ready');
      }
      return response;
    } catch (error) {
      const status = error.response?.status;
      // Preserve identity on uncertain outcomes and busy saves. A conclusive
      // validation/permission rejection can safely begin a corrected attempt.
      if ([400, 401, 403, 404, 422].includes(status)
          || error.response?.headers?.['x-business-command-state'] === 'rolled_back') clearIntent?.();
      if (status === 423) {
        const detail = error.response?.data?.detail;
        signalSaveStatus('pending', typeof detail === 'string' ? detail : 'A save is pending. Do not create a duplicate entry.');
      }
      throw error;
    }
  };

  let promise;
  if (inflight < MAX_CONCURRENT) {
    // Execute immediately
    inflight++;
    _emit('FIRE', { label });
    promise = run().finally(() => { inflight--; if (dk) inflightGets.delete(dk); _emit('COMPLETE', { label }); drain(); });
  } else {
    // Enqueue
    _emit('ENQUEUE', { label, queueLen: queue.length + 1 });
    promise = new Promise((resolve, reject) => {
      queue.push({ run, priority: isWrite ? 1 : 0, resolve, reject, label });
    }).finally(() => { if (dk) inflightGets.delete(dk); });
  }

  if (dk) inflightGets.set(dk, promise);
  return promise;
}

// ── Debounce ────────────────────────────────────────────────────────────
const timers = new Map();
export function debounce(key, fn, ms = 300) {
  if (timers.has(key)) clearTimeout(timers.get(key));
  return new Promise((resolve, reject) => {
    timers.set(key, setTimeout(() => {
      timers.delete(key);
      _emit('DEBOUNCE_FIRE', { key, delayMs: ms });
      fn().then(resolve).catch(reject);
    }, ms));
  });
}

// ── Stats + log export ──────────────────────────────────────────────────
export function getQueueStats() {
  return { inflight, queued: queue.length, deduped: inflightGets.size, groups: cancelGroups.size };
}
export function getLogRing() {
  return _log.slice();
}
export function resetLogRing() {
  _log.length = 0;
  _seqId = 0;
}
